import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { Add, DirectionsCar, Edit, HowToReg, LinkOff, MoreVert, Title } from '@mui/icons-material';
import {
    Box,
    Card,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Typography,
} from '@mui/material';
import { isFProject } from 'config/project';
import useContextMenu from 'hooks/useContextMenu';
import { PartyModel, PartyType } from 'models/PartyModel';
import { StockModel, StockStatus } from 'models/StockModel';
import { CompanyType } from 'models/CompanyModel';
import usePartyModals from 'modules/parties/hooks/usePartyModals';
import auth from 'recoil/atoms/auth';


type PartiesCardProps = {
    partyableType: string,
    partyable: StockModel,
}

const partyTypes = Object.values(PartyType);

const PartiesCard = ({partyableType, partyable}: PartiesCardProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const {
        openAddPartyModal,
        openEditPartyModal,
        confirmDetachParty,
        openChangePartyTypeModal,
        openInviteManagerModal,
    } = usePartyModals();
    const navigate = useNavigate();

    const canEdit = partyableType !== PartyType.Dealer && my.type !== CompanyType.Shipper;

    const {openMenu: openEdit} = useContextMenu<PartyModel>(({value, close}) => [
        canEdit && (
            <MenuItem
                key="edit"
                onClick={() => {
                    openEditPartyModal(value);
                    close();
                }}
            >
                <ListItemIcon>
                    <Edit fontSize="small"/>
                </ListItemIcon>
                {my.permissions.has(`parties update ${value.type}`) ? t('edit') : t('view')}
            </MenuItem>
        ),

        my.permissions.has('parties detach') && (
            <MenuItem
                key="detach"
                onClick={() => {
                    confirmDetachParty(partyableType, partyable.id, value);
                    close();
                }}
            >
                <ListItemIcon>
                    <LinkOff fontSize="small"/>
                </ListItemIcon>
                {t('detach')}
            </MenuItem>
        ),

        my.type === CompanyType.Master && (
            <MenuItem
                key="type"
                onClick={() => {
                    openChangePartyTypeModal(value);
                    close();
                }}
            >
                <ListItemIcon>
                    <Title fontSize="small"/>
                </ListItemIcon>
                {t('change_type')}
            </MenuItem>
        ),

        my.type === CompanyType.Master && !value.manager && (
            <MenuItem
                key="invite"
                onClick={() => {
                    openInviteManagerModal(value);
                    close();
                }}
            >
                <ListItemIcon>
                    <HowToReg fontSize="small"/>
                </ListItemIcon>
                {t('invite_manager')}
            </MenuItem>
        ),

        my.type === CompanyType.Master && (
            <MenuItem
                key="cars"
                onClick={() => {
                    navigate(`/cars/party/${value.id}`);
                    close();
                }}
            >
                <ListItemIcon>
                    <DirectionsCar fontSize="small"/>
                </ListItemIcon>
                {t('cars')}
            </MenuItem>
        ),
    ]);

    if (isFProject || my.type === CompanyType.PortAgent)
        return null;

    const canAction = partyable.status === StockStatus.Pending || my.type === CompanyType.Master;

    const partyHolder = partyTypes.map(type => ({
        type,
        party: partyable.parties[type],
    }));

    return (
        <Card sx={{mb: 2}}>
            <List dense sx={{
                display: {md: 'flex'},
                '& .MuiListItem-root': {
                    alignItems: {md: 'flex-start'}
                },
                '& .MuiListItem-root:not(:last-child)': {
                    borderRight: {md: '2px solid #eeeeee'}
                },
                '& .MuiListItemSecondaryAction-root': {
                    top: {md: '16px'},
                }
            }}>
                {partyHolder.map(item => item.party ?
                    <PartyItem
                        key={item.type}
                        party={item.party}
                        action={canAction ? openEdit : undefined}
                        openInviteManagerModal={openInviteManagerModal}
                    /> :
                    <PartyAdd
                        key={item.type}
                        type={item.type}
                        action={canAction && my.permissions.has(`parties create ${item.type}`) ?
                            () => openAddPartyModal(partyableType, partyable.id, item.type, Object.values(partyable.parties).map(p => p?.email)) :
                            undefined
                        }
                    />)}
            </List>
        </Card>
    );
};


type PartyItemProps = {
    action?: (e: React.MouseEvent<HTMLElement>, p: PartyModel) => void,
}

const PartyAdd = ({type, action}: PartyItemProps & { type: string }) => {
    const {t} = useTranslation('cars');

    return (
        <ListItem
            secondaryAction={action &&
                <IconButton
                    edge="end"
                    aria-label="add party"
                    onClick={e => action(e, {} as PartyModel)}
                >
                    <Add/>
                </IconButton>
            }>
            <ListItemText>{t(type)}</ListItemText>
        </ListItem>
    )
};

const PartyItem = ({party, action, openInviteManagerModal}: PartyItemProps & { party: PartyModel, openInviteManagerModal: any, }) => {
    const {t} = useTranslation('cars');
    const navigate = useNavigate();

    return (
        <ListItem
            secondaryAction={action &&
                <IconButton
                    edge="end"
                    aria-label="party actions"
                    onClick={e => action(e, party)}
                >
                    <MoreVert/>
                </IconButton>
            }>
            <ListItemText
                primary={t(party.type)}
                secondaryTypographyProps={{component: 'div'}}
                secondary={
                    <>
                        <Box display="flex" gap={1} mb={1}>
                            {party.name}
                            <HowToReg
                                sx={{cursor: 'pointer'}}
                                color={party.manager ? 'success' : 'disabled'}
                                fontSize="small"
                                onClick={() => party.manager ? navigate(`/cars/party/${party.id}`) : openInviteManagerModal(party)}
                            />
                        </Box>

                        {party.type === PartyType.Consignee && (
                            <Typography fontSize=".75rem" color={party.address ? 'text.disabled' : 'error.light'}>
                                {party.address || t('no_address')}
                            </Typography>
                        )}

                        <Box>
                            {party.email && <a href={`mailto:${party.email}`}>{party.email}</a>}
                        </Box>
                        <Box>
                            {party.phone && <a href={`tel:${party.phone}`}>{party.phone}</a>}
                        </Box>
                    </>
                }
            />
        </ListItem>
    )
};

export default PartiesCard;
